import React, { useState, memo, useRef, useMemo } from 'react'
import { BoxProps, Button, ButtonVariant } from '@weareberyl/design-system'
import {
  Modal,
  Input,
  Checkbox,
  Cascader,
  Select,
  Form,
  Row,
  Col,
  Radio,
} from 'antd'
import { v4 as uuidv4 } from 'uuid'

import { formItemLayout } from '~/components/Job/Modal'
import { useJobTypes } from '~/hooks'
import { DASHBOARD_JUSTIFICATION_PREFIX } from '..'
import { isEqual } from 'lodash'
import { isNotNullOrUndefined } from '~/utils/types'
import {
  AssetCategory,
  DashboardAutomationsQuery_automations_Automation,
  JobAutomationSideEffect,
  TriggerDashboardAutomationMutationVariables,
} from '~/gql/generated/graphql'
import useSchemeJobAssets from '~/hooks/useSchemeJobAssets'
import SchemeSelect from '~/components/Scheme/SchemeSelect'
import { getAssetCategoryName } from '~/components/Job/utils'

const ALLOWED_JOB_TYPES = ['RD01', 'ST01']
const ALLOWED_ASSET_CATEGORIES = [
  AssetCategory.bike,
  AssetCategory.ebike,
  AssetCategory.scooter,
]

export type DashboardFlow = DashboardAutomationsQuery_automations_Automation & {
  scheme_id?: string
}

interface Props {
  modalOpen: boolean
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  automation: DashboardFlow
  runFlow: (
    variables: TriggerDashboardAutomationMutationVariables,
    text: string,
  ) => void
}

const DashboardFlowModal = ({
  modalOpen,
  setModalOpen,
  automation,
  runFlow,
}: Props) => {
  const [form] = Form.useForm()
  const schemeId = Form.useWatch('scheme_id', form)
  const categories = Form.useWatch('categories', form)
  const ref = useRef(null)
  const disabled = Boolean(automation)
  const [useIndividualAssets, setUseIndividualAssets] = useState(disabled)
  const { roleByJobLookup, jobOptions } = useJobTypes()
  const assets = useSchemeJobAssets(schemeId)

  const options = useMemo(() => {
    if (!assets) return []
    return assets
      .filter(({ assetCategory }) =>
        ALLOWED_ASSET_CATEGORIES.includes(assetCategory),
      )
      .map(({ assetTag, assetName }) => ({ value: assetTag, name: assetName }))
  }, [assets])

  const jobs = useMemo(
    () => automation?.jobs.filter(isNotNullOrUndefined) ?? [],
    [automation],
  )

  const affectedAssets = useMemo(
    () =>
      assets
        ?.filter(({ assetCategory }) => categories?.includes(assetCategory))
        .map(({ assetTag }) => assetTag) ?? [],
    [categories, assets],
  )

  const withDisabledJobOptions = useMemo(
    () =>
      jobOptions?.map(role => {
        let disabled = true
        const children = role.children?.map(child => {
          if (ALLOWED_JOB_TYPES.includes(child?.value)) {
            disabled = false
            return child
          }
          return { ...child, disabled: true }
        })
        return {
          ...role,
          disabled,
          children,
        }
      }) ?? [],
    [jobOptions],
  )

  if (!modalOpen) return null

  const onFinish = fields => {
    const [, taskId] = fields.taskId
    const variables = {
      taskId,
      description: fields.description,
      justification_id:
        automation?.justification_id ??
        `${DASHBOARD_JUSTIFICATION_PREFIX}-${uuidv4()}`,
      side_effects: fields.set_unavailable
        ? [JobAutomationSideEffect.set_unavailable]
        : [],
      results: (useIndividualAssets ? fields.assets : affectedAssets).map(
        frame_id => ({ scheme_id: parseInt(schemeId), frame_id }),
      ),
      dry_run: false,
    }

    if (
      !isEqual(
        variables.results.map(({ frame_id }) => frame_id),
        jobs.map(({ frame_id }) => frame_id),
      )
    ) {
      const text = automation
        ? 'Please confirm that you want to modify this flow'
        : 'Please confirm that you want to create this flow'
      runFlow(variables, text)
    }
    setModalOpen(false)
  }

  return (
    <Modal
      title={automation ? 'Edit Flow' : 'Create Flow'}
      open={modalOpen}
      onCancel={() => setModalOpen(false)}
      onOk={() => form.submit()}
    >
      <Form form={form} onFinish={onFinish} labelWrap>
        <Form.Item
          label="Scheme"
          name="scheme_id"
          initialValue={automation?.scheme_id}
          rules={[
            {
              required: true,
              message: 'Please select a scheme',
            },
          ]}
          hasFeedback
          {...formItemLayout}
        >
          <SchemeSelect
            scheme_id={String(automation?.scheme_id)}
            disabled={disabled}
          />
        </Form.Item>
        <Form.Item
          hidden={!schemeId}
          label="Description"
          name="description"
          initialValue={automation?.description}
          rules={[
            {
              required: true,
              message: 'Description required!',
            },
          ]}
          {...formItemLayout}
        >
          <Input disabled={disabled} />
        </Form.Item>
        <Form.Item
          hidden={!schemeId}
          label="Job"
          name="taskId"
          initialValue={
            automation?.taskId
              ? [roleByJobLookup?.[automation?.taskId], automation?.taskId]
              : ''
          }
          rules={[
            {
              required: true,
              message: 'Valid job type required!',
            },
          ]}
          {...formItemLayout}
        >
          <Cascader
            options={withDisabledJobOptions}
            disabled={disabled}
            ref={ref}
            expandTrigger="hover"
          />
        </Form.Item>
        <Form.Item
          hidden={!schemeId}
          label="Set Unavailable"
          name="set_unavailable"
          initialValue={Boolean(
            automation?.side_effects?.includes('set_unavailable'),
          )}
          valuePropName="checked"
          {...formItemLayout}
        >
          <Checkbox disabled={disabled} />
        </Form.Item>
        <Row style={{ marginBottom: 24 }} hidden={!schemeId || disabled}>
          <Col span={formItemLayout.labelCol.span} />
          <Col span={formItemLayout.wrapperCol.span}>
            <Radio.Group
              value={useIndividualAssets ? 'assets' : 'categories'}
              onChange={e =>
                setUseIndividualAssets(e.target.value === 'assets')
              }
              style={{ width: '100%' }}
            >
              <Radio.Button value="categories" style={{ width: '50%' }}>
                Categories
              </Radio.Button>
              <Radio.Button value="assets" style={{ width: '50%' }}>
                Assets
              </Radio.Button>
            </Radio.Group>
          </Col>
        </Row>
        <Form.Item
          hidden={!useIndividualAssets || !schemeId}
          label="Assets"
          name="assets"
          initialValue={jobs.map(({ frame_id }) => frame_id)}
          rules={[
            {
              required: useIndividualAssets,
              message: 'You must select a valid group of vehicles!',
            },
          ]}
          {...formItemLayout}
        >
          <Select
            mode="multiple"
            options={options}
            tokenSeparators={[',', ' ']}
            allowClear
          />
        </Form.Item>
        <Form.Item
          hidden={useIndividualAssets || !schemeId}
          label="Vehicle Categories"
          name="categories"
          initialValue={jobs.map(({ frame_id }) => frame_id)}
          rules={[
            {
              required: !useIndividualAssets,
              message: 'You must select a valid vehicle category!',
            },
            {
              validator: () => {
                if (affectedAssets.length > 0 || useIndividualAssets)
                  return Promise.resolve()
                return Promise.reject(
                  new Error('Selection must include at least one vehicle!'),
                )
              },
            },
          ]}
          {...formItemLayout}
        >
          <Select
            mode="multiple"
            options={ALLOWED_ASSET_CATEGORIES.map(value => ({
              value,
              label: getAssetCategoryName(value),
            }))}
            tokenSeparators={[',', ' ']}
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

const CreateDashboardFlowButton = ({
  automation,
  runFlow,
  ...props
}: {
  automation?: DashboardFlow
  runFlow: (
    variables: TriggerDashboardAutomationMutationVariables,
    text: string,
  ) => void
  variant?: ButtonVariant
} & BoxProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const title = automation ? 'Edit' : 'Create Flow'
  return (
    <>
      <Button title={title} onPress={() => setModalOpen(true)} {...props} />
      <DashboardFlowModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        automation={automation}
        runFlow={runFlow}
      />
    </>
  )
}

export default memo(CreateDashboardFlowButton)
